import { IList } from 'annotation-tools/utils';
import { Observable } from 'rxjs';
import {
  filter,
  map,
  mapTo,
  scan,
  startWith,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';
import {
  AnnotationCanvasActionType,
  IBboxType,
  IdType,
  ILabelType
} from '../imports/annotation-canvas';
import {
  pageIndexReducerDynamic,
  pageIndexReducerDynamicInitialState,
  visibleLabelsReducer
} from '../imports/ui-parts';
import {
  filterEnumSubset,
  filterNotUndefined,
  filterValue,
  get,
  IRect,
  IRectType,
  isInRange,
  mapToConst,
  mergeTyped,
  useDataStream,
  useStream
} from '../imports/utils';
import { AppMode } from '../types/enum/app-mode';
import { ToolbarButtonIdEnum } from '../types/enum/toolbar-button-id-enum';
import { useKeyEventStreams } from './sub/use-key-event-streams';

export const useStreams = (
  /* from props */
  bboxRelativeListInput$: Observable<IList<IBboxType>>,
  cropRectRelativeInput$: Observable<IRectType>,
  labels$: Observable<IList<ILabelType>>,
  pageIndexInput$: Observable<number>,
  pageLength$: Observable<number>,
  /* events */
  appModeChange$: Observable<AppMode>,
  toolbarButtonClick$: Observable<ToolbarButtonIdEnum>,
  pageChange$: Observable<number>,
  labelClick$: Observable<IdType>,
  visibilityClick$: Observable<IdType>,
  showAllClick$: Observable<'show-all'>,
  hideAllClick$: Observable<'hide-all'>,
  closeHelpDialog$: Observable<void>,
  confirmDialogResult$: Observable<'yes' | 'no'>,
  bboxesFromAnnotationCanvas$: Observable<IList<IBboxType>>,
  cropRectRelativeFromCanvas$: Observable<IRectType>
) => {
  const {
    zKeyWithCtrl$,
    upperZKeyWithCtrl$,
    numKey$,
    deleteKeyIsDown$
  } = useKeyEventStreams();

  const helpDialogIsOpen$ = useDataStream<boolean>(
    false,
    mergeTyped(
      toolbarButtonClick$.pipe(filterValue('help'), mapTo(true)),
      closeHelpDialog$.pipe(mapTo(false))
    )
  );

  const confirmDialogIsOpen$ = useDataStream<boolean>(
    false,
    mergeTyped(
      toolbarButtonClick$.pipe(filterValue('remove-all'), mapTo(true)),
      confirmDialogResult$.pipe(mapTo(false))
    )
  );

  const currentPageIndex$ = useDataStream<number>(
    0,
    mergeTyped(
      pageLength$.pipe(
        map(pageLength => ({ op: 'page-length' as const, pageLength }))
      ),
      pageIndexInput$.pipe(map(index => ({ op: 'input' as const, index }))),
      pageChange$.pipe(map(index => ({ op: 'input' as const, index })))
    ).pipe(
      scan(pageIndexReducerDynamic, pageIndexReducerDynamicInitialState),
      get('currentPage')
    )
  );

  const selectedLabel$ = useDataStream<ILabelType | undefined>(
    undefined,
    mergeTyped(
      numKey$.pipe(
        withLatestFrom(labels$),
        filter(([numKey, labels]) => isInRange(1, labels.size)(numKey)),
        map(([numKey, labels]) => labels.get(numKey - 1))
      ),
      labelClick$.pipe(
        withLatestFrom(labels$),
        map(([labelId, labels]) => labels.find(e => e.id === labelId))
      ),
      appModeChange$.pipe(filterValue('crop-image'), mapTo(undefined)) // deselect label on readonly mode
    )
  );

  const visibleLabels$ = useDataStream<IList<ILabelType>>(
    IList<ILabelType>(),
    labels$.pipe(
      switchMap(labels =>
        mergeTyped(
          showAllClick$,
          hideAllClick$,
          visibilityClick$.pipe(
            map(labelId => labels.findIndex(e => e.id === labelId)),
            filter(idx => idx >= 0)
          )
        ).pipe(
          scan(visibleLabelsReducer, labels.map(() => true).toArray()),
          map(flags => labels.filter((_, i) => flags[i])),
          startWith(labels)
        )
      )
    )
  );

  const appMode$ = useDataStream<AppMode>(
    'crop-image',
    mergeTyped(
      appModeChange$,
      selectedLabel$.pipe(filterNotUndefined(), mapToConst('rectbox'))
    )
  );

  const bboxRelativeList$ = useDataStream<{
    lastOp: 'input' | 'canvas';
    bboxList: IList<IBboxType>;
  }>(
    { lastOp: 'input', bboxList: IList<IBboxType>() },
    mergeTyped(
      bboxRelativeListInput$.pipe(
        map(bboxList => ({
          lastOp: 'input' as const,
          bboxList
        }))
      ),
      bboxesFromAnnotationCanvas$.pipe(
        map(bboxList => ({
          lastOp: 'canvas' as const,
          bboxList
        }))
      )
    )
  );

  const cropRectRelative$ = useDataStream<{
    lastOp: 'input' | 'canvas';
    cropRect: IRectType;
  }>(
    { lastOp: 'input', cropRect: IRect() },
    mergeTyped(
      cropRectRelativeInput$.pipe(
        map(cropRectRelativeList => ({
          lastOp: 'input' as const,
          cropRect: cropRectRelativeList
        }))
      ),
      cropRectRelativeFromCanvas$.pipe(
        map(cropRectRelative => ({
          lastOp: 'canvas' as const,
          cropRect: cropRectRelative
        }))
      )
    )
  );

  const action$ = useStream<AnnotationCanvasActionType>(
    mergeTyped(
      toolbarButtonClick$.pipe(
        filterEnumSubset<
          ToolbarButtonIdEnum,
          AnnotationCanvasActionType['op'] & ToolbarButtonIdEnum
        >(['undo', 'redo', 'remove-selected'])
      ),
      confirmDialogResult$.pipe(filterValue('yes'), mapToConst('remove-all')),
      zKeyWithCtrl$.pipe(mapToConst('undo')),
      upperZKeyWithCtrl$.pipe(mapToConst('redo')),
      deleteKeyIsDown$.pipe(mapToConst('remove-selected')),
      currentPageIndex$.pipe(mapToConst('reset-zoom')),
      currentPageIndex$.pipe(mapToConst('reset-pointer-pos'))
    ).pipe(map(op => ({ op })))
  );

  /* inference */

  return {
    currentPageIndex$,
    bboxRelativeList$,
    /* annotation tool state */
    selectedLabel$,
    visibleLabels$,
    appMode$,
    /* annotation tool action */
    action$,
    /* dialogs */
    helpDialogIsOpen$,
    confirmDialogIsOpen$,
    /* crop image */
    cropRectRelative$
  };
};
